import isMobile from "is-mobile";
import videojs from "video.js";
import { computed, onUnmounted, Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useCurrentCustomerName } from "../project";

export const usePlayerZoom = (playerInstance: Ref<videojs.Player | null>) => {
  const customerName = useCurrentCustomerName();

  // TODO: add to configuration (glockle asked to disable for them)
  const isGloeckleProject = ["gloeckle", "gloecklebau"].includes(customerName);

  if (isMobile() || isGloeckleProject) {
    return;
  }

  const { t } = useI18n();

  const zoomLevel = ref(1);
  const panX = ref(0);
  const panY = ref(0);
  const isPanning = ref(false);
  const startPan = ref({ x: 0, y: 0 });
  const startOffset = ref({ x: 0, y: 0 });

  const isMacos = navigator.userAgent.toLowerCase().includes("mac os x");
  const ctrlKey = isMacos ? "⌘ Cmd" : "Ctrl";

  const videoEl = computed(() => playerInstance.value?.el()?.querySelector("video"));

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 4;
  const ZOOM_STEP = 0.5;

  const clampPan = () => {
    if (!videoEl.value) {
      return;
    }

    const container = videoEl.value.parentElement;

    if (!container) {
      return;
    }

    const { width: contW, height: contH } = container.getBoundingClientRect();
    const scale = zoomLevel.value;

    const scaledW = contW * scale;
    const scaledH = contH * scale;

    const maxOffsetX = Math.max(0, scaledW - contW);
    const maxOffsetY = Math.max(0, scaledH - contH);

    panX.value = Math.min(0, Math.max(-maxOffsetX, panX.value));
    panY.value = Math.min(0, Math.max(-maxOffsetY, panY.value));
  };

  const applyTransform = () => {
    clampPan();

    const transition = isPanning.value ? "none" : "transform 0.1s ease-out";
    const cursor = zoomLevel.value > 1 ? (isPanning.value ? "grabbing" : "grab") : "";
    const transform = `translate(${panX.value}px, ${panY.value}px) scale(${zoomLevel.value})`;

    if (videoEl.value) {
      videoEl.value.style.transition = transition;
      videoEl.value.style.transform = transform;
      videoEl.value.style.transformOrigin = "0 0";
      videoEl.value.style.cursor = cursor;
    }

    const canvasEl = playerInstance.value?.el()?.querySelector("canvas");
    if (canvasEl) {
      canvasEl.style.transition = transition;
      canvasEl.style.transform = transform;
      canvasEl.style.transformOrigin = "0 0";
      canvasEl.style.cursor = cursor;
    }
  };

  const onWheel = (e: WheelEvent) => {
    if (!videoEl.value || !(e.ctrlKey || e.metaKey) || Math.abs(e.deltaY) < 1) {
      return;
    }

    e.preventDefault();

    const container = videoEl.value.parentElement!;
    const rect = container.getBoundingClientRect();

    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;

    const oldZoom = zoomLevel.value;

    let newZoom = oldZoom * (1 - Math.sign(e.deltaY) * (ZOOM_STEP / 2));
    newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

    if (newZoom === oldZoom) {
      return;
    }

    const scaleFactor = newZoom / oldZoom;

    panX.value = panX.value - (cursorX - panX.value) * (scaleFactor - 1);
    panY.value = panY.value - (cursorY - panY.value) * (scaleFactor - 1);

    zoomLevel.value = newZoom;

    applyTransform();
  };

  const zoomAtPoint = (zoomDelta: number, originX: number, originY: number) => {
    const oldZoom = zoomLevel.value;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldZoom + zoomDelta));

    if (newZoom === oldZoom) {
      return;
    }

    const scaleFactor = newZoom / oldZoom;

    panX.value = panX.value - (originX - panX.value) * (scaleFactor - 1);
    panY.value = panY.value - (originY - panY.value) * (scaleFactor - 1);

    zoomLevel.value = newZoom;

    applyTransform();
  };

  const zoomIn = () => {
    const container = videoEl.value?.parentElement;

    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    zoomAtPoint(ZOOM_STEP, centerX, centerY);
  };

  const zoomOut = () => {
    const container = videoEl.value?.parentElement;

    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    zoomAtPoint(-ZOOM_STEP, centerX, centerY);
  };

  const resetZoom = () => {
    zoomLevel.value = 1;
    panX.value = 0;
    panY.value = 0;

    applyTransform();
  };

  const onPanStart = (e: MouseEvent) => {
    if (zoomLevel.value === 1) {
      return;
    }

    isPanning.value = true;

    startPan.value = { x: e.clientX, y: e.clientY };
    startOffset.value = { x: panX.value, y: panY.value };

    window.addEventListener("mousemove", onPanMove);
    window.addEventListener("mouseup", onPanEnd);
  };

  const onPanMove = (e: MouseEvent) => {
    if (!isPanning.value) {
      return;
    }

    const dx = e.clientX - startPan.value.x;
    const dy = e.clientY - startPan.value.y;

    panX.value = startOffset.value.x + dx;
    panY.value = startOffset.value.y + dy;

    applyTransform();
  };

  const onPanEnd = () => {
    isPanning.value = false;

    window.removeEventListener("mousemove", onPanMove);
    window.removeEventListener("mouseup", onPanEnd);

    try {
      if (playerInstance.value?.paused()) {
        playerInstance.value?.play();
      } else {
        playerInstance.value?.pause();
      }
    } catch (_) {
      // Ignore  if player is not ready
    }

    applyTransform();
  };

  const setUpVideoElement = () => {
    zoomLevel.value = 1;
    panX.value = 0;
    panY.value = 0;
    isPanning.value = false;

    if (!playerInstance.value) {
      return;
    }

    document.addEventListener("fullscreenchange", resetZoom);

    if (videoEl.value) {
      videoEl.value.addEventListener("mousedown", onPanStart);
      videoEl.value.addEventListener("wheel", onWheel, { passive: false });

      applyTransform();
    }
  };

  const createButton = (name: string, label: string, tooltip: string, onClick: () => void) => {
    const Button = videojs.getComponent("Button");

    class CustomButton extends Button {
      constructor(player: videojs.Player, options?: videojs.ComponentOptions) {
        super(player, options);

        this.controlText(tooltip);

        const el = this.el() as HTMLElement;

        el.innerHTML = label;
        el.style.fontSize = "18px";
        el.style.width = "32px";
        el.style.display = "flex";
        el.style.alignItems = "center";
        el.style.justifyContent = "center";
        el.style.fontSize = "24px";

        if (name === "ZoomOutButton") {
          el.style.transform = "scaleX(1.5)";
        }

        if (name !== "ZoomInButton") {
          el.style.paddingBottom = "3px";
        } else {
          el.style.paddingTop = "2px";
        }
      }
    }
    videojs.registerComponent(name, CustomButton);

    if (playerInstance.value) {
      const button = playerInstance.value.controlBar.addChild(
        name,
        {},
        playerInstance.value.controlBar.children().length - 1,
      );

      button.on("click", onClick);
    }
  };

  const setupButtons = () => {
    try {
      if (playerInstance.value) {
        playerInstance.value.controlBar.getChild("ZoomInButton")?.dispose();
        playerInstance.value.controlBar.getChild("ZoomOutButton")?.dispose();
        playerInstance.value.controlBar.getChild("ZoomResetButton")?.dispose();
      }
    } catch {
      // ignore if buttons missing
    }

    createButton("ZoomOutButton", "-", `${ctrlKey} + ${t("video_player.zoom_in")}`, zoomOut);
    createButton("ZoomInButton", "+", `${ctrlKey} + ${t("video_player.zoom_out")}`, zoomIn);
    createButton("ZoomResetButton", "⟳", t("video_player.reset_zoom"), resetZoom);
  };

  const clearListeners = () => {
    window.removeEventListener("mousemove", onPanMove);
    window.removeEventListener("mouseup", onPanEnd);
    document.removeEventListener("fullscreenchange", resetZoom);

    if (videoEl.value) {
      videoEl.value.removeEventListener("wheel", onWheel);
      videoEl.value.removeEventListener("mousedown", onPanStart);
    }
  };

  watch(
    () => playerInstance.value,
    () => {
      clearListeners();

      if (playerInstance.value) {
        setUpVideoElement();
        setupButtons();
      }
    },
  );

  onUnmounted(() => {
    clearListeners();
  });
};
